import { useState, useEffect, useCallback, useRef } from "react";

// useApi — fetch on mount / dependency change, with loading + error state.
//
// THE CONTROLLER GUARDS STATE, IT DOES NOT CANCEL THE REQUEST. `fn` is called
// with no arguments and the signal is never handed to fetch, so a superseded
// request still runs to completion on the network — what the controller buys
// is that its result is discarded instead of overwriting a newer one. That is
// the property callers actually depend on (no stale overwrite); the comment
// here used to claim cancellation, which sent people looking for a bug when a
// request they expected to be aborted showed up in the network panel.
//
// Wiring real cancellation means threading the signal through api/client.js to
// every call site's fn() — worth doing, but it is a change to the API client's
// contract, not to this hook.
export function useApi(fn, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);
    try {
      const result = await fn();
      if (!ctrl.signal.aborted) setData(result);
    } catch (e) {
      if (!ctrl.signal.aborted) setError(e.message);
    } finally {
      if (!ctrl.signal.aborted) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    load();
    return () => abortRef.current?.abort();
  }, [load]);

  return { data, loading, error, reload: load };
}
